import { Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import notify, { NotificationType } from "@common/helpers/NotificationManager";
import { Toast } from "@app/elements/text/Alert";

export function NotificationContainer() {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [bottomOffset, setBottomOffset] = useState("1.25rem");

  useEffect(() => {
    notify.callback = (notifications) => setNotifications(notifications);
    setNotifications(notify.listNotify);

    return () => (notify.callback = undefined);
  }, []);

  useEffect(() => {
    let observedComposer: HTMLElement | null = null;
    const resizeObserver = new ResizeObserver(() => updateBottomOffset());

    const updateBottomOffset = () => {
      const composer = document.querySelector<HTMLElement>(
        "[data-reply-composer='true']",
      );
      if (composer !== observedComposer) {
        if (observedComposer) {
          resizeObserver.unobserve(observedComposer);
        }
        if (composer) {
          resizeObserver.observe(composer);
        }
        observedComposer = composer;
      }

      const rect = composer?.getBoundingClientRect();
      const isComposerVisible =
        rect &&
        rect.height > 0 &&
        rect.bottom > 0 &&
        rect.top < window.innerHeight;

      const baseOffset =
        window.matchMedia("(max-width: 767px)").matches
          ? "calc(5.75rem + env(safe-area-inset-bottom))"
          : "1.25rem";

      setBottomOffset(
        isComposerVisible
          ? `${Math.max(20, window.innerHeight - rect.top + 12)}px`
          : baseOffset,
      );
    };

    updateBottomOffset();

    const mutationObserver = new MutationObserver(updateBottomOffset);

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener("resize", updateBottomOffset);
    window.addEventListener("scroll", updateBottomOffset, true);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("resize", updateBottomOffset);
      window.removeEventListener("scroll", updateBottomOffset, true);
    };
  }, [notifications.length]);

  return (
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-x-3 bottom-5 z-toast flex justify-start sm:left-5 sm:right-auto sm:w-[420px]"
      style={{ bottom: bottomOffset }}
    >
      <div className="flex w-full flex-col gap-2">
        {notifications.map((notification) => (
          <Notification
            key={notification.id}
            notification={notification}
            onRequestHide={() => notify.remove(notification)}
          />
        ))}
      </div>
    </div>
  );
}

function Notification({
  notification,
  onRequestHide,
}: {
  notification: NotificationType;
  onRequestHide: () => void;
}) {
  const [show, setShow] = React.useState(true);

  useEffect(() => {
    if (notification.timeout !== false) {
      const timer = setTimeout(
        () => setShow(false),
        notification.timeout || 3000,
      );
      return () => clearTimeout(timer);
    }
  }, [notification.timeout]);

  return (
    <Transition
      show={show}
      as={Fragment}
      appear
      enter="transform ease-out duration-200 transition"
      enterFrom="-translate-y-2 opacity-0 scale-[0.98]"
      enterTo="translate-y-0 opacity-100 scale-100"
      leave="transition-all ease-in duration-150"
      leaveFrom="opacity-100 max-h-[220px] scale-100"
      leaveTo="opacity-0 max-h-0"
      afterLeave={() => onRequestHide()}
    >
      <Toast
        action={notification.action}
        message={notification.message}
        onClose={() => setShow(false)}
        title={notification.title}
        type={notification.type}
      />
    </Transition>
  );
}
